import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { useImageUpload } from '@/hooks/useImageUpload';
import { useOrganization, useOrganizations } from '@/hooks/useOrganizations';
import { updateOrganizationMutation } from '@/queries/organization.query';
import { CameraSolid } from '@mynaui/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Separator } from './ui/separator';

interface OrganizationEditorProps {
  onClose?: () => void;
}

interface FormValues {
  name: string;
  logoFile: File | null;
  removeLogo: boolean;
}

export function OrganizationEditor({ onClose }: OrganizationEditorProps) {
  const organization = useOrganization();
  const queryClient = useQueryClient();
  const { refreshOrganizations } = useOrganizations();
  const { uploadImage, isUploading, uploadProgress } = useImageUpload({
    organizationId: organization?.id || '',
    type: 'ORGANIZATION_LOGO',
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const initialValues: FormValues = {
    name: organization?.name || '',
    logoFile: null,
    removeLogo: false,
  };

  const hasChanges = (values: FormValues) => {
    const nameChanged = values.name !== (organization?.name || '');
    const logoChanged = values.logoFile !== null;
    const logoRemoved = values.removeLogo;
    return nameChanged || logoChanged || logoRemoved;
  };

  const mutation = useMutation({
    mutationFn: async (input: { name?: string; logoId?: string | null }) => {
      if (!organization?.id) throw new Error('No organization ID');
      return execute(updateOrganizationMutation, { id: organization.id, input });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Organization updated successfully');

      formik.resetForm();
      setLogoPreview(null);
      setIsEditing(false);
      refreshOrganizations();
      onClose?.();
    },
    onError: (error) => {
      toast.error(`Failed to update organization: ${error.message}`);
    },
  });

  const handleSubmit = async (values: FormValues) => {
    if (!organization?.id) {
      toast.error('No organization selected');
      return;
    }

    if (!hasChanges(values)) {
      toast.info('No changes to save');
      return;
    }

    try {
      let logoId: string | undefined;

      if (values.logoFile) {
        try {
          logoId = await uploadImage(values.logoFile);
        } catch (error) {
          toast.error('Failed to upload logo. Please try again or contact support.');
          console.error('Logo upload error:', error);
          return;
        }
      }

      const updateInput: { name?: string; logoId?: string | null } = {};

      if (values.name !== (organization?.name || '')) {
        updateInput.name = values.name.trim();
      }

      if (logoId) {
        updateInput.logoId = logoId;
      } else if (values.removeLogo && organization.logo?.url) {
        updateInput.logoId = null;
      }

      await mutation.mutateAsync(updateInput);
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error(`Failed to update organization: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const _handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      formik.setFieldValue('logoFile', file);
      const url = URL.createObjectURL(file);
      setLogoPreview(url);
      setIsEditing(true);
    }
  };

  const _handleCancel = () => {
    setIsEditing(false);
    setLogoPreview(null);
    formik.resetForm();
  };

  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  if (!organization) {
    return (
      <Card>
        <CardContent className='p-6'>
          <div className='text-center text-gray-500'>No organization selected</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Settings</CardTitle>
        <CardDescription>
          Update your organization's name and logo. Only organization admins can make these changes.
        </CardDescription>
      </CardHeader>
      <CardContent className='gap-6'>
        <div className='flex items-center gap-6'>
          <div className='relative'>
            <img
              src={logoPreview || organization.logo?.url || getPlaceholderAvatar(organization.name || organization.id)}
              alt=''
              className='size-20 object-cover rounded-full border border-slate-200/70 dark:border-stone-800/70'
            />
            <label
              htmlFor='logo-upload'
              className={`absolute -bottom-1 -right-1 bg-slate-900 hover:bg-slate-950 text-white dark:bg-white dark:text-stone-900 rounded-full p-2 transition-colors ${
                isUploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              }`}>
              <CameraSolid className='size-4 stroke-2' />
              <input
                id='logo-upload'
                type='file'
                accept='image/*'
                className='hidden'
                onChange={_handleLogoUpload}
                disabled={isUploading}
              />
            </label>
            {isUploading && (
              <div className='absolute inset-0 flex items-center justify-center bg-black/50 rounded-full'>
                <span className='text-white text-xs font-medium'>{uploadProgress}%</span>
              </div>
            )}
          </div>
          <div className='flex flex-col gap-1.5'>
            <h3 className='text-lg font-medium leading-none text-slate-900 dark:text-white'>{organization.name}</h3>
            <p className='text-slate-500 dark:text-stone-400 text-sm font-medium leading-none'>
              {organization.slug}
            </p>
            {logoPreview && (
              <p className='text-xs text-slate-600 dark:text-stone-400'>New logo selected</p>
            )}
          </div>
        </div>
        <Separator />
        <form onSubmit={formik.handleSubmit}>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='space-y-2'>
              <Label htmlFor='name'>Organization Name</Label>
              <Input
                id='name'
                name='name'
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder='Enter organization name'
                required
                disabled={!isEditing}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='slug'>Slug</Label>
              <Input id='slug' value={organization.slug || ''} disabled />
            </div>
          </div>
          <div className='flex justify-end space-x-2 mt-4'>
            {!isEditing ? (
              <Button type='button' onClick={() => setIsEditing(true)}>
                Change Details
              </Button>
            ) : (
              <>
                <Button
                  type='button'
                  variant='outline'
                  onClick={_handleCancel}
                  disabled={isUploading || mutation.isPending}>
                  Cancel
                </Button>
                <Button type='submit' disabled={!hasChanges(formik.values) || isUploading || mutation.isPending}>
                  {isUploading || mutation.isPending ? (
                    <>
                      <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                      {isUploading ? `Uploading... ${Math.round(uploadProgress)}%` : 'Saving...'}
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
